/**
 * ═══════════════════════════════════════════════════════════════════════════
 * createPowerSyncQuery — Puente PowerSync ↔ TanStack Solid Query
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Equivalente Solid de los hooks `usePowerSyncQuery` que viven en
 * `@space/powersync` (React-only). Usa `db.onChangeWithCallback()` para que
 * la query se invalide automáticamente cuando alguna de las tablas
 * dependientes cambie, integrándose con TanStack Solid Query para compartir
 * caché con las mutaciones (API/oRPC) que ya viven en `@space/shared`.
 *
 * Referencia:
 *   - apps/native/.../power-sync-provider.tsx
 *   - packages/powersync/src/query.ts (versión React)
 *   - SOLID_MIGRATION_ROADMAP.md, Fase 4
 */

import type { AbstractPowerSyncDatabase, CompilableQuery, SyncStatus } from "@powersync/common";
import { toCompilableQuery } from "@powersync/drizzle-driver";
import type { Query } from "drizzle-orm";
import {
  createQuery,
  useQueryClient as getSolidQueryClient,
  type QueryKey,
} from "@tanstack/solid-query";
import { type Accessor, createEffect, createSignal, onCleanup } from "solid-js";
import { isDev } from "../utils/platform";

// ═══════════════════════════════════════════════════════════════════════════
// Runtime helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * True when the JS is running inside a Tauri WebView. We branch on this in a
 * few places because the alpha `@powersync/tauri-plugin` SDK never sets
 * `currentStatus.hasSynced = true` — relying on it as a "wake up" signal for
 * watched queries causes them to stay frozen in Silent Sync forever.
 */
function isTauriRuntime(): boolean {
  if (typeof globalThis === "undefined") {
    return false;
  }
  const win = (globalThis as { window?: unknown }).window;
  if (typeof win !== "object" || win === null) {
    return false;
  }
  return "__TAURI_INTERNALS__" in win;
}

// ═══════════════════════════════════════════════════════════════════════════
// Tipos
// ═══════════════════════════════════════════════════════════════════════════

export type PowerSyncTableRecord = object;

/**
 * Minimal reactive query result interface shared by both code paths in
 * `createPowerSyncQuery`:
 *   - the signal-based path (`suspense === false`), and
 *   - the standard TanStack `createQuery` path.
 *
 * `CreateQueryResult<T[], Error>` from TanStack is a structural superset of
 * this interface, so both branches satisfy it without casts.
 */
export interface CreatePowerSyncQueryResult<T extends PowerSyncTableRecord> {
  readonly data: T[] | undefined;
  readonly isFetching: boolean;
  readonly isLoading: boolean;
  readonly isPending: boolean;
  readonly error: Error | null;
  readonly isError: boolean;
  readonly status: "error" | "pending" | "success";
  readonly isPlaceholderData?: boolean;
  refetch(): void;
}

export type PowerSyncQueryFn<T extends PowerSyncTableRecord> = () =>
  | CompilableQuery<T>
  | { execute(): Promise<T | T[]>; toSQL(): Query }
  | null;

export interface CreatePowerSyncQueryArgs<T extends PowerSyncTableRecord> {
  /** Clave única de TanStack Query. Se reutiliza para invalidaciones cross-cliente. */
  queryKey: Accessor<QueryKey> | QueryKey;
  /** Función que retorna la query compilable (Drizzle o SQL raw). */
  queryFn: PowerSyncQueryFn<T>;
  /** Instancia de PowerSync (suele venir del provider). */
  db: Accessor<AbstractPowerSyncDatabase | null>;
  /** Opcional: deshabilitar selectivamente. */
  enabled?: Accessor<boolean> | boolean;
  /** Opcional: datos de marcador de posición (ej. keepPreviousData). */
  placeholderData?: T[] | ((prev: T[] | undefined) => T[] | undefined);
  /** Opcional: tiempo en ms para considerar los datos como frescos. */
  staleTime?: number;
  /** Opcional: tiempo en ms para mantener los datos en caché tras desuso. */
  gcTime?: number;
  /** Opcional: tiempo en ms para agrupar invalidaciones (batching). Por defecto 150ms. */
  throttleTime?: number;
  /**
   * Opcional: si la query debe integrarse con Solid Suspense.
   *
   * Por defecto `false` (no-suspensiva). Esta es una app local-first: las
   * lecturas de SQLite resuelven en ~ms, por lo que suspender el árbol para
   * mostrar un fallback hace MÁS daño (flash + remonte) que renderizar el
   * contenido directo con `keepPreviousData`. Pasa `suspense: true` solo donde
   * realmente quieras bloquear con un `<Suspense>` mientras carga.
   */
  suspense?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Constantes
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_THROTTLE_MS = 150;
const QUERY_DEBOUNCE_MS = 50;

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

function isAccessor<V>(value: Accessor<V> | V): value is Accessor<V> {
  return typeof value === "function";
}

function resolveAccessor<V>(value: Accessor<V> | V): V {
  return isAccessor(value) ? value() : value;
}

// The set of tables a query reads depends only on its SQL *shape*, never on the
// bound parameter values (`... WHERE list_id = ?` always touches the same
// tables). PowerSync's `resolveTables` resolves it with an `EXPLAIN` + a
// `sqlite_master` lookup on every (re)subscribe — so switching lists re-runs
// that exact pair of queries for SQL it has already resolved. Cache the result
// by SQL string (stable for the app session) so a list switch reuses it instead
// of hitting SQLite again.
// We cache the in-flight *promise* (not the resolved value) so that several
// queries sharing the same SQL — e.g. the main header and sticky header both
// running the same `count(...)` on a list switch — dedupe onto a single
// `resolveTables` call instead of each firing its own EXPLAIN before the first
// completes. A rejected lookup is evicted so it can be retried.
const resolvedTablesCache = new Map<string, Promise<string[]>>();

// Intentionally NOT async: it must synchronously store the in-flight promise in
// the cache before any await so concurrent callers dedupe onto it — an async
// function can't reference its own returned promise to cache it.
// oxlint-disable-next-line typescript/promise-function-async
function resolveTablesCached(
  db: AbstractPowerSyncDatabase,
  sql: string,
  params: readonly unknown[] | undefined,
): Promise<string[]> {
  const cached = resolvedTablesCache.get(sql);
  if (cached) {
    return cached;
  }
  // Cache the promise synchronously (before awaiting) so concurrent callers
  // share it; evict on failure so a transient error can be retried.
  const pending = (async (): Promise<string[]> => {
    try {
      return await db.resolveTables(sql, params ? [...params] : undefined);
    } catch (err) {
      resolvedTablesCache.delete(sql);
      throw err;
    }
  })();
  resolvedTablesCache.set(sql, pending);
  return pending;
}

// ═══════════════════════════════════════════════════════════════════════════
// API pública
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Crea una query reactiva sobre PowerSync integrada con TanStack Solid Query.
 *
 * Estrategia:
 *   1. `createQuery` de TanStack para gestionar caché, error, loading.
 *   2. `queryFn` ejecuta la `CompilableQuery` con `db.execute()`.
 *   3. `db.onChangeWithCallback` invalida la query key en cuanto alguna
 *      tabla referenciada cambie, lo que dispara un refetch.
 *
 * @example
 * ```tsx
 * const todos = createPowerSyncQuery({
 *   queryKey: () => ["todos", userId()],
 *   queryFn: () => drizzleDb.select().from(todo).where(eq(todo.user_id, userId())),
 *   db: () => powerSyncCtx.db,
 *   enabled: () => Boolean(userId()),
 * });
 * ```
 */
export function createPowerSyncQuery<T extends PowerSyncTableRecord>(
  args: CreatePowerSyncQueryArgs<T>,
): CreatePowerSyncQueryResult<T> {
  function getEnabled(): boolean {
    if (!args.db()) {
      return false;
    }
    return resolveAccessor(args.enabled ?? true);
  }

  // ── Non-suspending signal-based path (DEFAULT) ─────────────────────────────
  // Local-first default: only opt INTO Solid Suspense with an explicit
  // `suspense: true`. Everything else takes the signal path so a sub-100ms
  // local read never blanks/flashes the tree behind a <Suspense> boundary.
  if (args.suspense !== true) {
    const [data, setData] = createSignal<T[]>([]);
    const [isFetching, setIsFetching] = createSignal(false);
    const [error, setError] = createSignal<Error | null>(null);
    const [trigger, setTrigger] = createSignal(0);

    // Mirror fetched rows into the TanStack cache under this query's key. The
    // suspending path registers a real `createQuery` (which populates the
    // cache); the signal path does not, so cache-only readers — e.g. the
    // dashboard's keyed `createQuery` observers with no queryFn — would
    // otherwise find no data and throw "Missing queryFn". Writing here keeps
    // those read-only mirrors working without suspending the shell.
    const signalQueryClient = getSolidQueryClient();

    // 1. Table change subscription
    createEffect(() => {
      const db = args.db();
      if (!db || !getEnabled()) {
        return;
      }

      const queryInput = args.queryFn();
      if (!queryInput) {
        return;
      }
      const query =
        "toSQL" in queryInput
          ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
          : queryInput;

      const compiled = query.compile();
      const params = compiled.parameters as readonly unknown[] | undefined;
      const abortController = new AbortController();
      let cancelled = false;
      const cleanups: (() => void)[] = [];

      let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
      const THROTTLE_MS = args.throttleTime ?? DEFAULT_THROTTLE_MS;

      // CC-15: the Silent-Sync main-thread protection belongs at subscribe time
      // (`subscribeToTables` defers the watch until `hasSynced` when connected),
      // NOT here. `onChange` only fires for real changes on a watched table once
      // we have subscribed. Dropping invalidations while `connected && !hasSynced`
      // discarded LOCAL writes made in the window after subscribing offline and
      // then reconnecting mid-session (e.g. creating an entity in a form), leaving
      // the query stale until a full reload. The throttle already caps the rate.
      const requestInvalidation = () => {
        if (throttleTimeout) {
          return;
        }

        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
          setTrigger((t) => t + 1);
        }, THROTTLE_MS);
      };

      async function subscribeToTables(currentDb: AbstractPowerSyncDatabase): Promise<void> {
        try {
          const tables = await resolveTablesCached(currentDb, compiled.sql, params);
          if (cancelled) {
            return;
          }

          const status = currentDb.currentStatus;
          if (!isTauriRuntime() && !status.hasSynced && status.connected) {
            const unregisterStatus = currentDb.registerListener({
              statusChanged: (s) => {
                if (s.hasSynced) {
                  setTrigger((t) => t + 1);
                  unregisterStatus();
                  void subscribeToTables(currentDb);
                }
              },
            });
            cleanups.push(() => {
              unregisterStatus();
            });
            return;
          }

          // See the equivalent comment in the TanStack path: the first onChange
          // from `triggerImmediate` is redundant with the data-fetching effect's
          // own initial run. Skip it to avoid a double fetch on mount.
          let isFirstChange = true;

          currentDb.onChangeWithCallback(
            {
              onChange: () => {
                if (isFirstChange) {
                  isFirstChange = false;
                  return;
                }
                requestInvalidation();
              },
              onError: (err: unknown) => {
                console.warn("[createPowerSyncQuery] watch error:", err);
              },
            },
            // `triggerImmediate` makes PowerSync emit the query's initial state
            // right after subscribing — the canonical way to load data without a
            // global invalidate. It also closes the startup race where data lands
            // during the async `resolveTables` gap. Identical on every platform.
            { tables, signal: abortController.signal, triggerImmediate: true },
          );
        } catch (err) {
          console.warn("[createPowerSyncQuery] resolveTables error:", err);
        }
      }

      void subscribeToTables(db);

      onCleanup(() => {
        cancelled = true;
        abortController.abort();
        if (throttleTimeout) {
          clearTimeout(throttleTimeout);
        }
        for (const cleanup of cleanups) {
          cleanup();
        }
      });
    });

    // 2. Data fetching execution
    createEffect(() => {
      trigger(); // track trigger for manual/sub re-fetches
      const db = args.db();
      const enabled = getEnabled();
      if (!db || !enabled) {
        setData([]);
        setIsFetching(false);
        setError(null);
        return;
      }

      const queryInput = args.queryFn();
      if (!queryInput) {
        setData([]);
        setIsFetching(false);
        setError(null);
        return;
      }

      const query =
        "toSQL" in queryInput
          ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
          : queryInput;

      setIsFetching(true);
      setError(null);

      const fetchController = new AbortController();
      let delayTimeout: ReturnType<typeof setTimeout> | null = null;

      delayTimeout = setTimeout(() => {
        delayTimeout = null;
        void (async () => {
          try {
            const compiled = query.compile();
            const start = isDev ? performance.now() : 0;

            const result = await query.execute();

            if (isDev) {
              const duration = performance.now() - start;
              console.log(
                `[Query] ${compiled.sql} | Params: ${JSON.stringify(compiled.parameters)} | Duration: ${duration.toFixed(2)}ms`,
              );
            }

            if (fetchController.signal.aborted) {
              return;
            }
            const arrayResult = Array.isArray(result) ? result : [result];
            setData(arrayResult);
            signalQueryClient.setQueryData(resolveAccessor(args.queryKey), arrayResult);
            setIsFetching(false);
          } catch (err) {
            if (fetchController.signal.aborted) {
              return;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsFetching(false);
          }
        })();
      }, QUERY_DEBOUNCE_MS);

      onCleanup(() => {
        fetchController.abort();
        if (delayTimeout) {
          clearTimeout(delayTimeout);
        }
      });
    });

    const result: CreatePowerSyncQueryResult<T> = {
      get data(): T[] | undefined {
        return data();
      },
      get isFetching(): boolean {
        return isFetching();
      },
      get isLoading(): boolean {
        return isFetching() && data().length === 0;
      },
      get isPending(): boolean {
        return isFetching() && data().length === 0;
      },
      get error(): Error | null {
        return error();
      },
      get isError(): boolean {
        return error() !== null;
      },
      get status(): "error" | "pending" | "success" {
        if (error()) {
          return "error";
        }
        if (isFetching()) {
          return "pending";
        }
        return "success";
      },
      refetch: () => {
        setTrigger((t) => t + 1);
      },
    };
    return result;
  }

  // ── Standard TanStack Query path ───────────────────────────────────────────
  const queryClient = getSolidQueryClient();

  function getQueryKey(): QueryKey {
    return resolveAccessor(args.queryKey);
  }

  // 1. Suscripción al cambio de tablas — invalida TanStack al detectar cambios.
  createEffect(() => {
    const db = args.db();
    if (!db) {
      return;
    }
    if (!getEnabled()) {
      return;
    }

    const queryInput = args.queryFn();
    if (!queryInput) {
      return;
    }
    const query =
      "toSQL" in queryInput
        ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
        : queryInput;

    const compiled = query.compile();
    const params = compiled.parameters as readonly unknown[] | undefined;
    const abortController = new AbortController();
    let cancelled = false;
    const cleanups: (() => void)[] = [];

    // Throttle helper para evitar ráfagas de invalidaciones
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    const THROTTLE_MS = args.throttleTime ?? DEFAULT_THROTTLE_MS;

    // CC-15: see the longer note on the signal-path `requestInvalidation`. The
    // Silent-Sync main-thread protection belongs at subscribe time
    // (`subscribeToTables` defers until `hasSynced` when connected), NOT here.
    // Dropping invalidations while `connected && !hasSynced` lost LOCAL writes
    // made after subscribing offline and then reconnecting mid-session, leaving
    // the query stale until a full reload. The throttle already caps the rate.
    const requestInvalidation = () => {
      if (throttleTimeout) {
        return;
      }

      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        void queryClient.invalidateQueries({ queryKey: getQueryKey() });
      }, THROTTLE_MS);
    };

    async function subscribeToTables(currentDb: AbstractPowerSyncDatabase): Promise<void> {
      try {
        const tables = await resolveTablesCached(currentDb, compiled.sql, params);
        if (cancelled) {
          return;
        }

        // Silent Sync: If we are in the initial sync phase, don't subscribe to
        // individual table changes yet. This prevents the main thread from
        // being overwhelmed by thousands of table change events.
        //
        // Skipped on Tauri: the alpha @powersync/tauri-plugin SDK never
        // surfaces `hasSynced = true` (the sync loop runs in Rust off the
        // main thread, and the bridged status event for `hasSynced` is not
        // implemented). If we kept Silent Sync gated on `hasSynced`, every
        // query would stay subscribed only to status events forever and
        // local writes would never refresh the UI. Tauri doesn't need the
        // optimization anyway — table-change events come pre-batched from
        // the Rust side, no risk of swamping the JS thread.
        const status = currentDb.currentStatus;
        if (!isTauriRuntime() && !status.hasSynced && status.connected) {
          console.log(`[PowerSync] Silent Sync active for query: ${JSON.stringify(getQueryKey())}`);

          // We subscribe to the STATUS change instead, to know when to "wake up"
          const unregisterStatus = currentDb.registerListener({
            statusChanged: (s) => {
              if (s.hasSynced) {
                console.log(
                  `[PowerSync] Initial sync complete. Waking up query: ${JSON.stringify(getQueryKey())}`,
                );
                void queryClient.invalidateQueries({ queryKey: getQueryKey() });
                unregisterStatus();
                void subscribeToTables(currentDb); // Re-subscribe normally
              }
            },
          });
          cleanups.push(() => {
            unregisterStatus();
          });
          return;
        }

        // Track whether the first onChange (from triggerImmediate) has fired.
        // `triggerImmediate` emits once on subscribe so PowerSync knows the
        // current table state — but TanStack Query's own mount already runs the
        // queryFn at that same moment.  Invalidating again creates a double
        // fetch on every component mount.  We skip only that very first call;
        // every subsequent onChange is a real DB change that must refetch.
        let isFirstChange = true;

        currentDb.onChangeWithCallback(
          {
            onChange: () => {
              if (isFirstChange) {
                isFirstChange = false;
                return;
              }
              requestInvalidation();
            },
            onError: (error: unknown) => {
              console.warn("[createPowerSyncQuery] watch error:", error);
            },
          },
          // `triggerImmediate`: emit the initial state on subscribe (loads data
          // without a global invalidate) and close the startup race where rows
          // land during the async `resolveTables` gap. Identical per platform.
          { tables, signal: abortController.signal, triggerImmediate: true },
        );
      } catch (error) {
        console.warn("[createPowerSyncQuery] resolveTables error:", error);
      }
    }

    void subscribeToTables(db);

    onCleanup(() => {
      cancelled = true;
      abortController.abort();
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      for (const cleanup of cleanups) {
        cleanup();
      }
    });
  });

  // 2. Query TanStack que ejecuta la SQL real.
  return createQuery(() => ({
    queryKey: getQueryKey(),
    enabled: getEnabled(),
    queryFn: async (context): Promise<T[]> => {
      const db = args.db();
      if (!db) {
        throw new Error("[createPowerSyncQuery] PowerSync database is not initialized");
      }
      const queryInput = args.queryFn();
      if (!queryInput) {
        return [];
      }
      const query =
        "toSQL" in queryInput
          ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
          : queryInput;

      if (context.signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      await new Promise<void>((resolve, reject) => {
        let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
        function onAbort() {
          if (timeout) {
            clearTimeout(timeout);
          }
          reject(new DOMException("Aborted", "AbortError"));
        }
        timeout = setTimeout(() => {
          context.signal.removeEventListener("abort", onAbort);
          resolve();
        }, QUERY_DEBOUNCE_MS);
        context.signal.addEventListener("abort", onAbort);
      });

      const compiled = query.compile();
      const start = isDev ? performance.now() : 0;

      const result = await query.execute();

      if (isDev) {
        const duration = performance.now() - start;
        console.log(
          `[Query] ${compiled.sql} | Params: ${JSON.stringify(compiled.parameters)} | Duration: ${duration.toFixed(2)}ms`,
        );
      }

      return result;
    },
    placeholderData: args.placeholderData,
    staleTime: args.staleTime,
    gcTime: args.gcTime,
    suspense: args.suspense,
  }));
}

/**
 * Variante para queries que devuelven un único registro (limit 1).
 */
export function createPowerSyncSingle<T extends PowerSyncTableRecord>(
  args: CreatePowerSyncQueryArgs<T>,
) {
  const query = createPowerSyncQuery<T>(args);

  return {
    get data(): T | null {
      return query.data?.[0] ?? null;
    },
    get isLoading(): boolean {
      return query.isLoading;
    },
    get isFetching(): boolean {
      return query.isFetching;
    },
    get error(): Error | null {
      return query.error;
    },
  };
}

/**
 * Crea un store reactivo que sigue el estado de sincronización de PowerSync.
 */
export function createPowerSyncStatus(db: Accessor<AbstractPowerSyncDatabase | null>) {
  const [status, setStatus] = createSignal<AbstractPowerSyncDatabase["currentStatus"] | null>(null);

  createEffect(() => {
    const currentDb = db();
    if (!currentDb) {
      setStatus(null);
      return;
    }

    setStatus(currentDb.currentStatus);
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;

    const unregister = currentDb.registerListener({
      statusChanged: (s) => {
        if (throttleTimeout) {
          return;
        }
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
          setStatus(s);
        }, DEFAULT_THROTTLE_MS);
      },
    });

    onCleanup(() => {
      unregister();
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    });
  });

  return status;
}

export interface PowerSyncStatusSnapshot {
  hasSynced: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  isDownloading: boolean;
  isUploading: boolean;
  isOnline: boolean;
  downloadedFraction: number | null;
  downloadedOperations: number | null;
  totalOperations: number | null;
}

const EMPTY_STATUS: PowerSyncStatusSnapshot = {
  hasSynced: false,
  isConnected: false,
  isConnecting: false,
  isDownloading: false,
  isUploading: false,
  isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
  downloadedFraction: null,
  downloadedOperations: null,
  totalOperations: null,
};

function snapshotFromStatus(
  status: SyncStatus | undefined,
  isOnline: boolean,
): PowerSyncStatusSnapshot {
  if (!status) {
    return { ...EMPTY_STATUS, isOnline };
  }
  const dl = status.dataFlowStatus;
  const progress = status.downloadProgress;
  return {
    hasSynced: status.hasSynced ?? false,
    isConnected: status.connected,
    isConnecting: status.connecting,
    isDownloading: dl.downloading ?? false,
    isUploading: dl.uploading ?? false,
    isOnline,
    downloadedFraction: progress?.downloadedFraction ?? null,
    downloadedOperations: progress?.downloadedOperations ?? null,
    totalOperations: progress?.totalOperations ?? null,
  };
}

/**
 * Hook reactivo extendido con snapshot de progreso y estado online.
 */
export function createExtendedPowerSyncStatus(db: Accessor<AbstractPowerSyncDatabase | null>) {
  const [status, setStatus] = createSignal<PowerSyncStatusSnapshot>(EMPTY_STATUS);

  // Browser online/offline
  createEffect(() => {
    const updateOnline = () => {
      const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
      setStatus((prev) => ({ ...prev, isOnline }));
    };
    globalThis.addEventListener("online", updateOnline);
    globalThis.addEventListener("offline", updateOnline);
    updateOnline();
    onCleanup(() => {
      globalThis.removeEventListener("online", updateOnline);
      globalThis.removeEventListener("offline", updateOnline);
    });
  });

  // Subscribe to PowerSync status changes
  createEffect(() => {
    const currentDb = db();
    if (!currentDb) {
      setStatus((prev) => ({ ...EMPTY_STATUS, isOnline: prev.isOnline }));
      return;
    }
    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
    setStatus(snapshotFromStatus(currentDb.currentStatus, isOnline));

    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = currentDb.registerListener({
      statusChanged: (s) => {
        if (throttleTimeout) {
          return;
        }
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
          const online = typeof navigator === "undefined" ? true : navigator.onLine;
          setStatus(snapshotFromStatus(s, online));
        }, DEFAULT_THROTTLE_MS);
      },
    });
    onCleanup(() => {
      unsubscribe();
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    });
  });

  return status;
}
